import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from 'api/Api';
import { User, UserAccessLevelPlanEnum } from 'api/UsersApi';

export interface AuthUser extends User {
  avatarUrl: string;
  isSysAdmin: boolean;
  isAdmin: boolean;
  hasInbox: boolean;
  tenantDomain: string;
  hipaaStatus: 'accepted' | 'revoked' | null;
  userId: string | null;
  patientProfileId: string | null;
  portalAccessStatus: 'profile_only' | 'invite_sent' | 'registered' | null;
  lifecycleStatus: string | null;
  tenantId: string | null;
  isOrganizationSponsoredAccess: boolean;
  effectiveAccessLevelPlan: UserAccessLevelPlanEnum;
}

const asStringOrNull = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

const asHipaaStatus = (value: unknown): AuthUser['hipaaStatus'] =>
  value === 'accepted' || value === 'revoked' ? value : null;

const asPortalAccessStatus = (value: unknown): AuthUser['portalAccessStatus'] =>
  value === 'profile_only' || value === 'invite_sent' || value === 'registered' ? value : null;

const asAccessLevelPlan = (value: unknown): UserAccessLevelPlanEnum =>
  value === UserAccessLevelPlanEnum.Premium || value === UserAccessLevelPlanEnum.Unlimited
    ? value
    : UserAccessLevelPlanEnum.Free;

const asBoolean = (value: unknown): boolean => value === true;

export const normalizeAuthUser = (payload: AuthUser): AuthUser => {
  const source = payload as AuthUser & Record<string, unknown>;

  return {
    ...payload,
    userId: asStringOrNull(source.userId) ?? asStringOrNull(source.id),
    patientProfileId: asStringOrNull(source.patientProfileId) ?? asStringOrNull(source.profileId),
    portalAccessStatus: asPortalAccessStatus(source.portalAccessStatus),
    lifecycleStatus: asStringOrNull(source.lifecycleStatus),
    hipaaStatus: asHipaaStatus(source.hipaaStatus),
    tenantId: asStringOrNull(source.tenantId),
    tenantOrganizationId: asStringOrNull(source.tenantOrganizationId),
    isOrganizationSponsoredAccess: asBoolean(source.isOrganizationSponsoredAccess),
    effectiveAccessLevelPlan: asAccessLevelPlan(source.effectiveAccessLevelPlan)
  };
};

export const invalidateAuthTags = () => authApi.util.invalidateTags(['Auth']);

export const authApi = createApi({
  baseQuery: baseQueryWithReAuth,
  reducerPath: 'authApi',
  tagTypes: ['Auth'],
  endpoints: (build) => ({
    login: build.mutation<string, { email: string; password: string }>({
      query: (body) => ({
        url: '/auth/login',
        method: 'POST',
        body
      }),
      transformResponse: (response: { data: { accessToken: string } }) => response.data.accessToken,
      invalidatesTags: ['Auth']
    }),
    loginGoogle: build.mutation<string, { accessToken: string; origin: string; partnerCode?: string }>({
      query: (body) => ({
        url: '/auth/login/google',
        method: 'POST',
        body
      }),
      transformResponse: (response: { data: { accessToken: string } }) => response.data.accessToken,
      invalidatesTags: ['Auth']
    }),
    loginFacebook: build.mutation<string, { accessToken: string; origin: string; partnerCode?: string }>({
      query: (body) => ({
        url: '/auth/login/facebook',
        method: 'POST',
        body
      }),
      transformResponse: (response: { data: { accessToken: string } }) => response.data.accessToken,
      invalidatesTags: ['Auth']
    }),
    signup: build.mutation<
      boolean,
      {
        firstName: string;
        lastName: string;
        email: string;
        password: string;
        code?: string;
        organizationCode?: string;
        partnerCode?: string;
        origin?: string;
      }
    >({
      query: (body) => ({
        url: '/auth/signup',
        method: 'POST',
        body
      }),
      transformResponse: (response: { success: boolean }) => response.success
    }),
    confirmEmail: build.mutation<boolean, { email: string; code: string }>({
      query: (body) => ({
        url: '/auth/confirm-email',
        method: 'POST',
        body
      }),
      transformResponse: (response: { success: boolean }) => response.success
    }),
    forgotPassword: build.mutation<boolean, { email: string }>({
      query: (body) => ({
        url: '/auth/forgot-password',
        method: 'POST',
        body
      }),
      transformResponse: (response: { success: boolean }) => response.success
    }),
    resetPassword: build.mutation<boolean, { email: string; password: string; code: string }>({
      query: (body) => ({
        url: '/auth/reset-password',
        method: 'POST',
        body
      }),
      transformResponse: (response: { success: boolean }) => response.success
    }),
    refreshToken: build.mutation<string | null, void>({
      query: () => ({
        url: '/auth/refresh-token',
        method: 'POST'
      }),
      transformResponse: (response: { data?: { accessToken?: string } }) => response?.data?.accessToken || null
    }),
    me: build.query<AuthUser, void>({
      query: () => ({ url: '/auth/me' }),
      transformResponse: (response: { data: AuthUser }) => normalizeAuthUser(response.data),
      providesTags: () => ['Auth']
    }),
    logout: build.mutation<boolean, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST'
      }),
      transformResponse: (response: { success: boolean }) => response.success
    })
  })
});

export const {
  useLoginMutation,
  useLoginGoogleMutation,
  useLoginFacebookMutation,
  useSignupMutation,
  useConfirmEmailMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useRefreshTokenMutation,
  useMeQuery,
  useLogoutMutation
} = authApi;
